import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { setAccessToken, logout } from './authSlice'; // ✅ IMPORT ACTIONS

// Base query with refresh token logic
const baseQuery = fetchBaseQuery({
  baseUrl: 'https://api.pengravity.com/api/instructor',
  prepareHeaders: (headers, { getState }) => {
    const token = getState()?.auth?.accessToken;
    console.log('🔐 Using token for API request:', token ? 'Yes' : 'No');
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('accept', 'application/json');
    } else {
      console.warn('⚠️ No access token found for API request');
    }
    return headers;
  },
});

// Base query with refresh token interceptor
const baseQueryWithRefresh = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  
  // Check if we got a 401 Unauthorized error (token expired)
  if (result.error && result.error.status === 401) {
    console.log('🔄 Access token expired, attempting to refresh...');
    
    // Get refresh token from state
    const refreshToken = api.getState().auth?.refreshToken;
    
    if (refreshToken) {
      try {
        // Call refresh token API
        const refreshResult = await baseQuery(
          {
            url: '/auth/refresh-token',
            method: 'POST',
            body: { token: refreshToken },
            headers: {
              'Content-Type': 'application/json',
            },
          },
          api,
          extraOptions
        );
        
        console.log('🔄 Refresh token response:', refreshResult);
        
        if (refreshResult.data && refreshResult.data.success && refreshResult.data.accessToken) {
          // Get new access token
          const newAccessToken = refreshResult.data.accessToken;
          
          console.log('✅ Got new access token, retrying original request...');
          
          // Update token in store
          api.dispatch(setAccessToken(newAccessToken));
          
          // Retry the original query with new token
          result = await baseQuery(args, api, extraOptions);
        } else {
          console.error('❌ Refresh token failed, logging out...');
          // Refresh failed - logout user
          api.dispatch(logout());
        }
      } catch (refreshError) {
        console.error('❌ Error during token refresh:', refreshError);
        api.dispatch(logout());
      }
    } else {
      console.error('❌ No refresh token available, logging out...');
      api.dispatch(logout());
    }
  }
  
  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithRefresh,
  tagTypes: ['Profile', 'Course', 'Section', 'Video', 'Withdrawal', 'Dashboard', 'Transaction', 'Students', 'Notifications', 'Search', 'BasicInfo', 'Quiz'],
  endpoints: (builder) => ({
    signup: builder.mutation({
      query: (formData) => ({
        url: '/auth/signup',
        method: 'POST',
        body: formData,
      }),
    }),
    login: builder.mutation({
      query: (credentials) => ({
        url: '/auth/signin',
        method: 'POST',
        body: credentials,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    
    // BASIC INFO QUERY
    getBasicInfo: builder.query({
      query: () => {
        console.log('🔄 Fetching basic info from API...');
        return '/auth/basic';
      },
      providesTags: ['BasicInfo'],
    }),
    
    getProfile: builder.query({
      query: () => {
        console.log('🔄 Fetching profile from API...');
        return '/auth/profile';
      },
      providesTags: ['Profile'],
    }),
    updateProfile: builder.mutation({
      query: (formData) => ({
        url: '/auth/update/profile',
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: ['Profile'],
    }),
    changePassword: builder.mutation({
      query: (passwordData) => ({
        url: '/auth/security',
        method: 'PUT',
        body: passwordData,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    forgetPassword: builder.mutation({
      query: (emailData) => ({
        url: '/auth/forget',
        method: 'POST',
        body: emailData,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    verifyOtp: builder.mutation({
      query: (otpData) => ({
        url: '/auth/otp',
        method: 'POST',
        body: otpData,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    resetPassword: builder.mutation({
      query: (passwordData) => ({
        url: '/auth/reset',
        method: 'POST',
        body: passwordData,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    
    // DASHBOARD QUERY - UPDATED
    getDashboard: builder.query({
      query: () => {
        console.log('🔄 Fetching dashboard data from API...');
        return '/feature/dashboard/home';
      },
      transformResponse: (response) => {
        console.log('📊 Dashboard API Response:', response);
        
        // Check if response has success flag
        if (response?.success !== true) {
          throw new Error(response?.message || 'Failed to fetch dashboard data');
        }
        
        // Transform data to match your frontend structure
        return {
          success: response.success,
          message: response.message,
          statsData: {
            totalStudents: response.topStats?.totalStudents || 0,
            totalCourses: response.topStats?.totalCourses || 0,
            totalEarnings: response.topStats?.totalEarnings || 0,
            pendingCourses: response.topStats?.pendingCourses || 0
          },
          recentCourses: response.recentCourses?.map(course => ({
            id: course.id,
            title: course.title,
            students: course.studentCount || 0,
            status: course.status,
            lastUpdated: course.updatedAt ? 
              new Date(course.updatedAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              }) : 'N/A'
          })) || [],
          recentTransactions: response.recentTransactions?.map(transaction => ({
            id: transaction.id || Math.random(),
            course: transaction.courseName || 'N/A',
            amount: transaction.amount || 0,
            date: transaction.createdAt ? 
              new Date(transaction.createdAt).toLocaleDateString('en-US') : 'N/A',
            status: transaction.status || 'Completed'
          })) || []
        };
      },
      transformErrorResponse: (error) => {
        console.error('❌ Dashboard API Error:', error);
        return {
          success: false,
          message: error.data?.message || 'Failed to load dashboard data',
          statsData: {
            totalStudents: 0,
            totalCourses: 0,
            totalEarnings: 0,
            pendingCourses: 0
          },
          recentCourses: [],
          recentTransactions: []
        };
      },
      providesTags: ['Dashboard'],
    }),

    // TRANSACTIONS QUERY
    getTransactions: builder.query({
      query: () => {
        console.log('🔄 Fetching transactions from API...');
        return '/feature/transactions';
      },
      providesTags: ['Transaction'],
    }),

    // WITHDRAWALS QUERY
    getWithdrawals: builder.query({
      query: () => {
        console.log('🔄 Fetching withdrawals from API...');
        return '/feature/withdrawals';
      },
      providesTags: ['Withdrawal'],
    }),

   // In your apiSlice.js - update the getCourseStudentsQuery
getCourseStudents: builder.query({
  query: ({ courseId, page = 1, limit = 10 }) => {
    console.log(`🔄 Fetching students for course ${courseId} - Page ${page}, Limit ${limit}`);
    return {
      url: `/feature/courses/${courseId}/students?page=${page}&limit=${limit}`,
      method: 'GET',
      headers: {
        'Accept': '*/*',
      },
    };
  },
  providesTags: (result, error, { courseId }) => 
    result?.students ? [
      ...result.students.map(({ id }) => ({ type: 'Students', id })),
      { type: 'Students', id: `COURSE_${courseId}` },
    ] : [{ type: 'Students', id: `COURSE_${courseId}` }],
}),

   
 // ✅ FIXED: UPDATE COURSE MUTATION - EXACTLY LIKE YOUR POSTMAN EXAMPLE
    updateCourse: builder.mutation({
      query: (courseData) => {
        console.log('🔄 Updating course with COMPLETE data:', courseData);
        
        const formData = new FormData();
        
        // Add courseId
        if (courseData.courseId) {
          formData.append('courseId', courseData.courseId.toString());
          console.log('📤 Appending courseId:', courseData.courseId);
        }
        
        // Add basic fields
        if (courseData.title) formData.append('title', courseData.title);
        if (courseData.subtitle) formData.append('subtitle', courseData.subtitle);
        if (courseData.description) formData.append('description', courseData.description);
        if (courseData.categoryId) formData.append('categoryId', courseData.categoryId.toString());
        if (courseData.subcategoryId) formData.append('subcategoryId', courseData.subcategoryId.toString());
        if (courseData.language) formData.append('language', courseData.language);
        if (courseData.level) formData.append('level', courseData.level);
        
        // Add price fields
        if (courseData.price) formData.append('price', courseData.price.toString());
        if (courseData.promoPrice) formData.append('promoPrice', courseData.promoPrice.toString());
        
        // ✅ STEP 1 DATA - EXACTLY LIKE YOUR POSTMAN EXAMPLE
        // objectives[0], objectives[1], etc.
        if (courseData.objectives && Array.isArray(courseData.objectives)) {
          courseData.objectives.forEach((obj, index) => {
            if (obj && obj.trim() && obj !== 'string') {
              formData.append(`objectives[${index}]`, obj.trim());
              console.log(`📤 Appending objectives[${index}]:`, obj.trim());
            }
          });
        } else if (typeof courseData.objectives === 'string' && courseData.objectives) {
          formData.append('objectives[0]', courseData.objectives);
          console.log('📤 Appending objectives[0]:', courseData.objectives);
        }
        
        // requirements[0], requirements[1], etc.
        if (courseData.requirements && Array.isArray(courseData.requirements)) {
          courseData.requirements.forEach((req, index) => {
            if (req && req.trim() && req !== 'string') {
              formData.append(`requirements[${index}]`, req.trim());
              console.log(`📤 Appending requirements[${index}]:`, req.trim());
            }
          });
        } else if (typeof courseData.requirements === 'string' && courseData.requirements) {
          formData.append('requirements[0]', courseData.requirements);
          console.log('📤 Appending requirements[0]:', courseData.requirements);
        }
        
        // audiences[0], audiences[1], etc.
        if (courseData.audiences && Array.isArray(courseData.audiences)) {
          courseData.audiences.forEach((aud, index) => {
            if (aud && aud.trim() && aud !== 'string') {
              formData.append(`audiences[${index}]`, aud.trim());
              console.log(`📤 Appending audiences[${index}]:`, aud.trim());
            }
          });
        } else if (typeof courseData.audiences === 'string' && courseData.audiences) {
          formData.append('audiences[0]', courseData.audiences);
          console.log('📤 Appending audiences[0]:', courseData.audiences);
        }
        
        // Handle thumbnail
        if (courseData.thumbnail instanceof File) {
          formData.append('thumbnail', courseData.thumbnail);
          console.log('📤 Appending thumbnail as file');
        } else if (courseData.thumbnailUrl) {
          formData.append('thumbnail', courseData.thumbnailUrl);
          console.log('📤 Appending thumbnail URL:', courseData.thumbnailUrl);
        }
        
        // Handle sections
        if (courseData.sections) {
          try {
            let sectionsData = courseData.sections;
            if (typeof sectionsData === 'string') {
              sectionsData = JSON.parse(sectionsData);
            }
            formData.append('sections', JSON.stringify(sectionsData));
            console.log('📤 Appending sections data');
          } catch (error) {
            console.error('Error processing sections data:', error);
            formData.append('sections', courseData.sections);
          }
        }

        console.log('📤 COMPLETE FormData entries:');
        for (let pair of formData.entries()) {
          if (pair[0] === 'thumbnail' && pair[1] instanceof File) {
            console.log(pair[0] + ': [File]');
          } else {
            console.log(pair[0] + ': ' + pair[1]);
          }
        }

        return {
          url: '/feature/course/update',
          method: 'PUT',
          body: formData,
        };
      },
      invalidatesTags: ['Course', 'Section', 'Dashboard'],
    }),
    
    // CREATE COURSE MUTATION
    createCourse: builder.mutation({
      query: ({ stepId, data }) => {
        const url = `/feature/course/create?id=${stepId}`;
        
        const formData = new FormData();
        
        Object.keys(data).forEach(key => {
          if (key === 'thumbnail' && data[key] instanceof File) {
            formData.append('thumbnail', data[key]);
          } else if (Array.isArray(data[key])) {
            formData.append(key, data[key].join(', '));
          } else if (data[key] !== null && data[key] !== undefined) {
            formData.append(key, data[key].toString());
          }
        });

        console.log(`📤 Submitting step ${stepId} with data:`, data);
        console.log(`📎 FormData entries:`);
        for (let pair of formData.entries()) {
          console.log(pair[0] + ': ' + pair[1]);
        }

        return {
          url: url,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Course', 'Dashboard'],
    }),

    createSection: builder.mutation({
      query: ({ courseId, name, parentId = null }) => {
        const requestBody = {
          courseId: parseInt(courseId, 10),
          name: name
        };
        
        // If parentId is provided, add it to create a subsection
        if (parentId) {
          requestBody.parentId = parseInt(parentId, 10);
        }

        console.log('📤 Creating section with data:', JSON.stringify(requestBody, null, 2));

        return {
          url: '/feature/section/create',
          method: 'POST',
          body: requestBody,
          headers: {
            'Content-Type': 'application/json',
          },
        };
      },
      invalidatesTags: ['Section', 'Course'],
    }),
    
     // ✅ FIXED: UPLOAD VIDEO MUTATION WITH islock FIELD
    uploadVideo: builder.mutation({
      query: ({ courseId, sectionId, title, description, islock, vimeoResult }) => {
        // Create a proper JSON object with islock field
        const requestBody = {
          courseId: parseInt(courseId, 10),
          sectionId: parseInt(sectionId, 10),
          title: title,
          description: description || '',
          islock: islock !== undefined ? islock : true, // Default to true if not provided
          vimeoResult: vimeoResult // This should be an object with videoUrl and duration
        };

        console.log('📤 Uploading video with JSON data:', JSON.stringify(requestBody, null, 2));

        return {
          url: '/feature/course/video/add',
          method: 'POST',
          body: requestBody,
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json',
          },
        };
      },
      invalidatesTags: ['Video', 'Course'],
    }),
    
    uploadPdf: builder.mutation({
      query: (formData) => {
        console.log('📤 Uploading PDF with form data:');
        for (let pair of formData.entries()) {
          console.log(pair[0] + ': ', pair[1]);
        }

        return {
          url: '/feature/video/pdf/add',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Video'],
    }),

    // DELETE SECTION MUTATION
    deleteSection: builder.mutation({
      query: (sectionId) => ({
        url: `/feature/section/${sectionId}`,
        method: 'DELETE',
        headers: {
          'accept': '*/*',
        },
      }),
      invalidatesTags: ['Section', 'Course'],
    }),

    // DELETE VIDEO MUTATION
    deleteVideo: builder.mutation({
      query: (videoId) => ({
        url: `/feature/course/video/${videoId}`,
        method: 'DELETE',
        headers: {
          'accept': '*/*',
        },
      }),
      invalidatesTags: ['Video', 'Course'],
    }),

    // DELETE PDF MUTATION
    deletePdf: builder.mutation({
      query: (pdfId) => ({
        url: `/feature/video/pdf/${pdfId}`,
        method: 'DELETE',
        headers: {
          'accept': '*/*',
        },
      }),
      invalidatesTags: ['Video'],
    }),

    // PUBLISH COURSE MUTATION
    publishCourse: builder.mutation({
      query: (courseId) => ({
        url: `/feature/course/publish/${courseId}`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['Course', 'Dashboard'],
    }),
    
  

    getCourses: builder.query({
      query: ({ status, page = 1, limit = 10 } = {}) => {
        const params = new URLSearchParams();
        
        // Only add status if it's not 'ALL' and has a value
        if (status && status !== 'ALL' && status !== 'undefined') {
          params.append('status', status);
        }
        
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        
        const queryString = params.toString();
        const url = queryString ? `/feature/courses?${queryString}` : '/feature/courses';
        
        return {
          url: url,
          method: 'GET',
          headers: {
            'Accept': '*/*',
          },
        };
      },
      providesTags: (result) =>
        result && result.courses
          ? [
              ...result.courses.map(({ id }) => ({ type: 'Course', id })),
              { type: 'Course', id: 'LIST' },
            ]
          : [{ type: 'Course', id: 'LIST' }],
    }),
    

    // GET COURSE DETAIL QUERY
    getCourseDetail: builder.query({
      query: (courseId) => ({
        url: `/feature/course/${courseId}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      providesTags: ['Course'],
    }),

    // GET CATEGORIES QUERY
    getCategories: builder.query({
      query: ({ page = 1, limit = 10 } = {}) => ({
        url: `/feature/categories?page=${page}&limit=${limit}`,
        method: 'GET',
        headers: {
          'accept': 'application/json',
        },
      }),
      providesTags: ['Category'],
    }),

    // GET SUBCATEGORIES QUERY
    getSubcategories: builder.query({
      query: ({ categoryId, page = 1, limit = 10 } = {}) => {
        if (!categoryId) {
          throw new Error('categoryId is required for subcategories query');
        }
        return {
          url: `/feature/subcategories?categoryId=${categoryId}&page=${page}&limit=${limit}`,
          method: 'GET',
          headers: {
            'accept': '*/*',
          },
        };
      },
      providesTags: ['Subcategory'],
    }),

    // CREATE WITHDRAWAL MUTATION
    createWithdrawal: builder.mutation({
      query: (withdrawalData) => ({
        url: '/feature/withdrawals/create',
        method: 'POST',
        body: withdrawalData,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['Withdrawal', 'Dashboard'],
    }),

    // NOTIFICATIONS ENDPOINT
    getNotifications: builder.query({
      query: () => {
        console.log('🔄 Fetching notifications from API...');
        return '/feature/notifications';
      },
      providesTags: ['Notifications'],
    }),

    // SEARCH ENDPOINT
    search: builder.query({
      query: (query) => {
        console.log('🔍 Searching with query:', query);
        return `/feature/search?q=${encodeURIComponent(query)}`;
      },
      providesTags: ['Search'],
    }),


createQuiz: builder.mutation({
  query: ({ title, videoId, sectionId }) => {
    const requestBody = { title };
    
    // Add videoId if provided
    if (videoId) {
      requestBody.videoId = parseInt(videoId, 10);
    }
    
    // Add sectionId if provided
    if (sectionId) {
      requestBody.sectionId = parseInt(sectionId, 10);
    }

    console.log('📤 Creating quiz with data:', JSON.stringify(requestBody, null, 2));

    return {
      url: '/feature/quizzes',
      method: 'POST',
      body: requestBody,
      headers: {
        'Content-Type': 'application/json',
        'accept': '*/*',
      },
    };
  },
  invalidatesTags: ['Quiz'],
}),

// ADD QUESTION TO QUIZ MUTATION
addQuestionToQuiz: builder.mutation({
  query: ({ quizId, question, options }) => ({
    url: `/feature/quizzes/${quizId}/questions`,
    method: 'POST',
    body: { question, options },
    headers: {
      'Content-Type': 'application/json',
      'accept': '*/*',
    },
  }),
  invalidatesTags: ['Quiz'],
}),

    // ✅ REORDER SECTION ITEMS MUTATION (DRAG AND DROP)
    reorderSectionItems: builder.mutation({
      query: ({ draggedItemId, targetItemId }) => {
        const requestBody = {
          draggedItemId: parseInt(draggedItemId, 10),
          targetItemId: parseInt(targetItemId, 10)
        };

        console.log('🔄 Reordering section items with data:', JSON.stringify(requestBody, null, 2));

        return {
          url: '/feature/section-item/reorder',
          method: 'PUT',
          body: requestBody,
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json',
          },
        };
      },
      // Invalidate both Course and Section tags to refresh data
      invalidatesTags: ['Course', 'Section'],
    }),


    // ✅ NEW API: TOGGLE SECTION LOCK/UNLOCK
    toggleSectionLock: builder.mutation({
      query: (sectionId) => {
        console.log(`🔒 Toggling lock status for section ID: ${sectionId}`);
        
        return {
          url: `/feature/section/flag/${sectionId}`,
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json',
          },
        };
      },
      // Transform the response to match your frontend needs
      transformResponse: (response) => {
        console.log('🔒 Section lock toggle response:', response);
        
        // The response from backend has "islock" field (lowercase L)
        // Make sure we handle it correctly
        return {
          success: response.success,
          message: response.message,
          data: {
            id: response.data?.id,
            name: response.data?.name,
            courseId: response.data?.courseId,
            parentId: response.data?.parentId,
            createdAt: response.data?.createdAt,
            updatedAt: response.data?.updatedAt,
            isLock: response.data?.islock // Map islock to isLock for frontend
          }
        };
      },
      invalidatesTags: (result, error, sectionId) => [
        { type: 'Section', id: sectionId },
        'Course' // Invalidate course to refresh section data
      ],
    }),

     // ✅ NEW API: DELETE STANDALONE PDF MUTATION (for PDFs attached to sections without video)
   deleteStandalonePdf: builder.mutation({
  query: (pdfId) => {
    console.log(`📄 Deleting standalone PDF with ID: ${pdfId} at /feature/pdf/${pdfId}`);
    return {
      url: `/feature/pdf/${pdfId}`,
      method: 'DELETE',
      headers: {
        'accept': '*/*',
      },
    };
  },
  // Invalidate Section and Course tags to refresh data
  invalidatesTags: (result, error, pdfId) => [
    { type: 'Pdf', id: pdfId },
    'Section',
    'Course'
  ],
}),

    // ✅ NEW API: REORDER ENTIRE SECTION (TOP, BOTTOM, UP, DOWN)
    reorderSection: builder.mutation({
      query: ({ sectionId, position }) => {
        const requestBody = {
          sectionId: parseInt(sectionId, 10),
          position: position // 'top', 'bottom', 'up', 'down'
        };

        console.log('🔄 Reordering entire section with data:', JSON.stringify(requestBody, null, 2));

        return {
          url: '/feature/section/reorder',
          method: 'PUT',
          body: requestBody,
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json',
          },
        };
      },
      // Invalidate Course and Section tags to refresh data
      invalidatesTags: ['Course', 'Section'],
    }),

    // ✅ FIXED: UPDATE COURSE FEATURES (VIDEOS, PDFs, QUIZZES) LOCK/UNLOCK STATUS
    updateCourseFeatures: builder.mutation({
      query: (updateData) => {
        console.log('🔒 Updating course features with data:', updateData);
        
        // Format the request body exactly like your cURL example
        const requestBody = {
          courseId: parseInt(updateData.courseId, 10),
          videoIds: [],  // This should be array of objects with id and islock
          pdfIds: [],    // This should be array of objects with id and islock
          quizIds: []    // This should be array of objects with id and islock
        };
        
        // Handle videoIds - check if it's array of objects or array of numbers
        if (updateData.videoIds) {
          if (Array.isArray(updateData.videoIds)) {
            if (updateData.videoIds.length > 0) {
              // Check if first item is an object with id property or just a number
              if (typeof updateData.videoIds[0] === 'object' && updateData.videoIds[0] !== null) {
                // Already in correct format {id: 76, islock: false}
                requestBody.videoIds = updateData.videoIds.map(v => ({
                  id: parseInt(v.id, 10),
                  islock: v.islock
                }));
              } else {
                // Need to convert from your component's format
                console.warn('⚠️ videoIds is array of numbers, but API expects objects with id and islock');
                // This case shouldn't happen with your fixed code
              }
            }
          } else if (typeof updateData.videoIds === 'object' && updateData.videoIds !== null) {
            // Single video object
            requestBody.videoIds = [{
              id: parseInt(updateData.videoIds.id, 10),
              islock: updateData.videoIds.islock
            }];
          }
        }
        
        // Handle pdfIds
        if (updateData.pdfIds) {
          if (Array.isArray(updateData.pdfIds)) {
            if (updateData.pdfIds.length > 0) {
              if (typeof updateData.pdfIds[0] === 'object' && updateData.pdfIds[0] !== null) {
                requestBody.pdfIds = updateData.pdfIds.map(p => ({
                  id: parseInt(p.id, 10),
                  islock: p.islock
                }));
              }
            }
          } else if (typeof updateData.pdfIds === 'object' && updateData.pdfIds !== null) {
            requestBody.pdfIds = [{
              id: parseInt(updateData.pdfIds.id, 10),
              islock: updateData.pdfIds.islock
            }];
          }
        }
        
        // Handle quizIds
        if (updateData.quizIds) {
          if (Array.isArray(updateData.quizIds)) {
            if (updateData.quizIds.length > 0) {
              if (typeof updateData.quizIds[0] === 'object' && updateData.quizIds[0] !== null) {
                requestBody.quizIds = updateData.quizIds.map(q => ({
                  id: parseInt(q.id, 10),
                  islock: q.islock
                }));
              }
            }
          } else if (typeof updateData.quizIds === 'object' && updateData.quizIds !== null) {
            requestBody.quizIds = [{
              id: parseInt(updateData.quizIds.id, 10),
              islock: updateData.quizIds.islock
            }];
          }
        }

        console.log('📤 Sending formatted request body:', JSON.stringify(requestBody, null, 2));

        return {
          url: '/feature/course/update-videos',
          method: 'POST',
          body: requestBody,
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json',
          },
        };
      },
      transformResponse: (response) => {
        console.log('✅ Update course features response:', response);
        return {
          success: response.success,
          videoIds: response.videoIds || [],
          pdfIds: response.pdfIds || [],
          quizIds: response.quizIds || []
        };
      },
      invalidatesTags: (result, error, { courseId }) => [
        { type: 'Course', id: courseId },
        'Section',
        'Video',
        'Quiz'
      ],
    }),

  }),
});

export const { 
  useSignupMutation, 
  useLoginMutation, 
  useGetBasicInfoQuery, // Add this
  useGetProfileQuery,
  useUpdateProfileMutation,
  useChangePasswordMutation,
  useForgetPasswordMutation,
  useVerifyOtpMutation,
  useResetPasswordMutation,
  useGetDashboardQuery,
  useGetTransactionsQuery,
  useGetWithdrawalsQuery,
  useGetCourseStudentsQuery,
  useUpdateCourseMutation,
  useCreateCourseMutation,
  useCreateSectionMutation,
  useUploadVideoMutation,
  useUploadPdfMutation,
  useDeleteSectionMutation,
  useDeleteVideoMutation,
  useDeletePdfMutation,
  usePublishCourseMutation,
  useGetCoursesQuery,
  useGetCourseDetailQuery,
  useGetCategoriesQuery,
  useGetSubcategoriesQuery,
  useCreateWithdrawalMutation,
  useGetNotificationsQuery,
  useSearchQuery,
  useCreateQuizMutation,
  useAddQuestionToQuizMutation,
  useReorderSectionItemsMutation,
  useToggleSectionLockMutation,
  useDeleteStandalonePdfMutation,
  useReorderSectionMutation,
  useUpdateCourseFeaturesMutation
} = apiSlice;